/* ==========================================================================
   Rook UI Core — Tabs Controller
   Web Component: <rk-tabs-element>
   ========================================================================== */

import { RkComponent } from '@rook/component';

class RkTabsElement extends RkComponent {
    static hydration = 'eager';

    setup() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        this.activeTabId = null;

        if (this.tabs.length === 0 || this.panels.length === 0) return;

        this.#setupA11y();

        this.delegate({
            'click [role="tab"]': this.onClick,
            'keydown [role="tab"]': this.onKeyDown,
        });

        const defaultId = this.dataset.defaultValue;
        const tabToActivate = this.tabs.find((tab) => tab.id === defaultId) || this.tabs[0];

        if (tabToActivate) {
            this.activateTab(tabToActivate.id, false);
        }
    }

    #setupA11y() {
        const tabList = this.querySelector('.rk-tabs__list');
        if (tabList && !tabList.hasAttribute('role')) {
            tabList.setAttribute('role', 'tablist');
        }

        this.tabs.forEach((tab) => {
            tab.setAttribute('tabindex', '-1');
            tab.setAttribute('aria-selected', 'false');
        });

        this.panels.forEach((panel) => {
            panel.setAttribute('tabindex', '0');
            if (!panel.hasAttribute('hidden')) {
                panel.setAttribute('hidden', 'true');
            }
        });
    }

    onClick(event, target) {
        if (target.disabled || target.getAttribute('aria-disabled') === 'true') return;
        this.activateTab(target.id, true);
    }

    onKeyDown(event, target) {
        const currentIndex = this.tabs.indexOf(target);
        let nextIndex = currentIndex;
        const orientation = this.dataset.orientation || 'horizontal';

        switch (event.key) {
            case 'ArrowRight':
                if (orientation === 'horizontal') nextIndex = currentIndex + 1;
                break;
            case 'ArrowLeft':
                if (orientation === 'horizontal') nextIndex = currentIndex - 1;
                break;
            case 'ArrowDown':
                if (orientation === 'vertical') nextIndex = currentIndex + 1;
                break;
            case 'ArrowUp':
                if (orientation === 'vertical') nextIndex = currentIndex - 1;
                break;
            case 'Home':
                nextIndex = 0;
                break;
            case 'End':
                nextIndex = this.tabs.length - 1;
                break;
            default:
                return;
        }

        event.preventDefault();

        if (nextIndex >= this.tabs.length) {
            nextIndex = 0;
        } else if (nextIndex < 0) {
            nextIndex = this.tabs.length - 1;
        }

        const nextTab = this.tabs[nextIndex];
        if (nextTab.disabled || nextTab.getAttribute('aria-disabled') === 'true') {
            return;
        }

        nextTab.focus();
        this.activateTab(nextTab.id, true);
    }

    activateTab(tabId, focus = false) {
        if (this.activeTabId === tabId) return;

        this.tabs.forEach((tab) => {
            const isSelected = tab.id === tabId;
            tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
            tab.setAttribute('tabindex', isSelected ? '0' : '-1');

            if (isSelected && focus) {
                tab.focus();
            }
        });

        this.panels.forEach((panel) => {
            const controlledBy = panel.getAttribute('aria-labelledby');
            if (controlledBy === tabId) {
                panel.removeAttribute('hidden');
                panel.classList.add('rk-tabs__panel--active');
            } else {
                panel.setAttribute('hidden', 'true');
                panel.classList.remove('rk-tabs__panel--active');
            }
        });

        this.activeTabId = tabId;
        this.rkEmit('tabs:change', { activeTabId: tabId });
    }
}

if (!customElements.get('rk-tabs-element')) {
    customElements.define('rk-tabs-element', RkTabsElement);
}
